import type { Router } from 'vue-router';
import { dedupeDuplicatedTabId } from '@shared/adminSessionTabId';
import { useAdminSessionStore } from '@shared/stores/adminSession.store';
import { useFeaturesStore } from '@shared/stores/features.store';

// Only studio-admin routes participate in the single-session lease. `/admin` on its
// own is the Millers-internal area, and everything else (consumer, cart, account) must
// never claim or probe — studios legitimately open many consumer tabs from admin links.
const ADMIN_STUDIO_PATH = /^\/admin\/studios\/(\d+)/;

/**
 * Wires the single-admin-session heartbeat (singleAdminSession feature flag) into the
 * router: start the lease heartbeat on studio-admin routes, stop it elsewhere, and
 * best-effort release the lease when the tab closes.
 */
export function installAdminSessionGuard(router: Router): void {
  // A duplicated tab copies our sessionStorage GUID; regenerate it before the first
  // heartbeat can impersonate the original tab. Stores aren't touched here, so this
  // is safe to run before Pinia is installed.
  dedupeDuplicatedTabId();

  router.afterEach(async (to) => {
    const adminSessionStore = useAdminSessionStore();
    const match = ADMIN_STUDIO_PATH.exec(to.path);

    if (!match) {
      adminSessionStore.stop({ release: true });
      return;
    }

    const studioId = Number(match[1]);
    try {
      const featuresStore = useFeaturesStore();
      await featuresStore.initializeWithStudioId(studioId);

      if (featuresStore.hasFeature('singleAdminSession')) {
        adminSessionStore.start(studioId);
      } else {
        // Flag off (or turned off since the last route): reset any leftover state,
        // but don't release — this tab never held a lease worth releasing.
        adminSessionStore.stop({ release: false });
      }
    } catch {
      // afterEach doesn't handle rejections, so a failed features fetch would surface
      // as an unhandled-rejection console error. Leave the store as it was — if this
      // tab is genuinely blocked, the page's own API calls 409 and the interceptor
      // raises the modal anyway.
    }
  });

  // pagehide fires on close/navigate-away and is the most reliable of the unload
  // events; the keepalive fetch inside survives the page teardown.
  window.addEventListener('pagehide', () => {
    useAdminSessionStore().releaseOnUnload();
  });
}
